/**
 * Минимальный разбор OLE2/CFB-контейнера (MS-CFB) — оболочки, в которой лежат старые
 * бинарные .xls: нужен, чтобы достать из файла поток `Workbook`.
 */

const SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
/** Первый спецмаркер: всё, что >= него (ENDOFCHAIN, FREESECT, ...), не является номером сектора. */
const MAX_REGULAR_SECTOR = 0xfffffffa;
const DIRECTORY_ENTRY_SIZE = 128;
const ENTRY_TYPE_STREAM = 2;

export function isCfb(bytes: Uint8Array): boolean {
  return SIGNATURE.every((byte, i) => bytes[i] === byte);
}

function concat(chunks: Uint8Array[], size?: number): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  chunks.forEach((chunk) => {
    result.set(chunk, offset);
    offset += chunk.length;
  });
  return size === undefined ? result : result.subarray(0, size);
}

/** Обход цепочки секторов по таблице размещения (FAT или miniFAT). */
function readChain(
  source: Uint8Array,
  chain: number[],
  start: number,
  sectorSize: number,
  offsetOf: (sector: number) => number,
): Uint8Array[] {
  const chunks: Uint8Array[] = [];
  const visited = new Set<number>();
  let sector = start;
  while (sector < MAX_REGULAR_SECTOR && !visited.has(sector)) {
    visited.add(sector);
    const offset = offsetOf(sector);
    if (offset + sectorSize > source.length) {
      break;
    }
    chunks.push(source.subarray(offset, offset + sectorSize));
    sector = chain[sector] ?? MAX_REGULAR_SECTOR;
  }
  return chunks;
}

/** Все потоки контейнера по именам директории (например, `Workbook`). */
export function readCfbStreams(bytes: Uint8Array): Map<string, Uint8Array> {
  if (!isCfb(bytes)) {
    throw new Error('Файл не является OLE2-контейнером');
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const sectorSize = 1 << view.getUint16(0x1e, true);
  const miniSectorSize = 1 << view.getUint16(0x20, true);
  const fatSectorCount = view.getUint32(0x2c, true);
  const directoryStart = view.getUint32(0x30, true);
  const miniStreamCutoff = view.getUint32(0x38, true);
  const miniFatStart = view.getUint32(0x3c, true);
  const difatStart = view.getUint32(0x44, true);

  if ((sectorSize !== 512 && sectorSize !== 4096) || miniSectorSize !== 64) {
    throw new Error('Повреждённый OLE2-контейнер');
  }
  const totalSectors = Math.ceil(bytes.length / sectorSize);
  const fatSectorLimit = Math.min(fatSectorCount, totalSectors);

  const sectorOffset = (sector: number) => (sector + 1) * sectorSize;

  // DIFAT: 109 записей в заголовке, остальное — в отдельных секторах
  const fatSectors: number[] = [];
  for (let i = 0; i < 109 && fatSectors.length < fatSectorLimit; i++) {
    const sector = view.getUint32(0x4c + i * 4, true);
    if (sector >= MAX_REGULAR_SECTOR) {
      break;
    }
    fatSectors.push(sector);
  }
  const perDifatSector = sectorSize / 4 - 1;
  let difatSector = difatStart;
  const visitedDifat = new Set<number>();
  while (difatSector < MAX_REGULAR_SECTOR && fatSectors.length < fatSectorLimit && !visitedDifat.has(difatSector)) {
    visitedDifat.add(difatSector);
    const offset = sectorOffset(difatSector);
    for (let i = 0; i < perDifatSector && fatSectors.length < fatSectorLimit; i++) {
      const sector = view.getUint32(offset + i * 4, true);
      if (sector < MAX_REGULAR_SECTOR) {
        fatSectors.push(sector);
      }
    }
    difatSector = view.getUint32(offset + perDifatSector * 4, true);
  }

  const fat: number[] = [];
  fatSectors.forEach((sector) => {
    const offset = sectorOffset(sector);
    for (let i = 0; i < sectorSize / 4; i++) {
      fat.push(view.getUint32(offset + i * 4, true));
    }
  });

  const directory = concat(readChain(bytes, fat, directoryStart, sectorSize, sectorOffset));
  const directoryView = new DataView(directory.buffer, directory.byteOffset, directory.byteLength);

  // Корневая запись описывает mini-поток, в котором лежат короткие потоки целиком
  const miniStream = concat(readChain(bytes, fat, directoryView.getUint32(0x74, true), sectorSize, sectorOffset));
  const miniFatBytes = concat(readChain(bytes, fat, miniFatStart, sectorSize, sectorOffset));
  const miniFatView = new DataView(miniFatBytes.buffer, miniFatBytes.byteOffset, miniFatBytes.byteLength);
  const miniFat: number[] = [];
  for (let i = 0; i + 4 <= miniFatBytes.length; i += 4) {
    miniFat.push(miniFatView.getUint32(i, true));
  }

  const entryCount = Math.floor(directory.length / DIRECTORY_ENTRY_SIZE);
  const rootEntries: number[] = [];
  const pending = [directoryView.getUint32(0x4c, true)];
  const visited = new Set<number>();
  while (pending.length) {
    const index = pending.pop();
    if (index >= entryCount || visited.has(index)) {
      continue;
    }
    visited.add(index);
    rootEntries.push(index);
    const offset = index * DIRECTORY_ENTRY_SIZE;
    pending.push(directoryView.getUint32(offset + 0x44, true), directoryView.getUint32(offset + 0x48, true));
  }

  const streams = new Map<string, Uint8Array>();
  for (const index of rootEntries) {
    const offset = index * DIRECTORY_ENTRY_SIZE;
    if (directory[offset + 0x42] !== ENTRY_TYPE_STREAM) {
      continue;
    }
    const nameLength = directoryView.getUint16(offset + 0x40, true);
    const name = new TextDecoder('utf-16le').decode(directory.subarray(offset, offset + Math.max(nameLength - 2, 0)));
    const start = directoryView.getUint32(offset + 0x74, true);
    const size = directoryView.getUint32(offset + 0x78, true);
    const data =
      size < miniStreamCutoff
        ? concat(
            readChain(miniStream, miniFat, start, miniSectorSize, (sector) => sector * miniSectorSize),
            size,
          )
        : concat(readChain(bytes, fat, start, sectorSize, sectorOffset), size);
    streams.set(name, data);
  }
  return streams;
}
